import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { listUserDocuments, getDocumentUrl, formatFileSize, Document } from '../services/documentService';

const Pharmacy: React.FC = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      const docs = await listUserDocuments(user?.userId || '', undefined, 'pharmacy');
      setDocuments(docs);
    } catch (err: any) {
      setError(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc: Document) => {
    try {
      const url = await getDocumentUrl(doc.key);
      window.open(url, '_blank');
    } catch (err: any) {
      setError(err.message || 'Failed to download file');
    }
  };

  return (
    <div className="pharmacy">
      <div className="page-header">
        <h1>Pharmacy</h1>
        <p>Your medication records and prescription history.</p>
      </div>

      <div className="content-card">
        <div className="card-header">
          <h2>Pharmacy Records</h2>
        </div>

        {error && <div className="error-message">{error}</div>}

        {loading ? (
          <p className="loading-text">Loading documents...</p>
        ) : documents.length > 0 ? (
          <div className="documents-list">
            {documents.map((doc) => (
              <div key={doc.key} className="document-item">
                <div className="doc-icon">💊</div>
                <div className="doc-info">
                  <span className="doc-name">{doc.name}</span>
                  <span className="doc-meta">
                    {formatFileSize(doc.size)} • {new Date(doc.lastModified).toLocaleDateString()}
                  </span>
                </div>
                <div className="doc-actions">
                  <button className="action-btn" onClick={() => handleDownload(doc)}>
                    View
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="empty-state">No pharmacy records uploaded yet.</p>
        )}
      </div>

      <style>{`
        .pharmacy p {
          color: var(--text-muted);
          line-height: 1.6;
          margin-bottom: 1rem;
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }
        .card-header h2 {
          margin: 0;
        }
        .documents-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }
        .document-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 1rem;
          background: var(--bg);
          border: 1px solid var(--border);
          border-radius: 12px;
          transition: border-color 0.15s;
        }
        .document-item:hover {
          border-color: var(--primary);
        }
        .doc-icon {
          font-size: 1.5rem;
        }
        .doc-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
        }
        .doc-name {
          color: var(--text);
          font-weight: 600;
        }
        .doc-meta {
          color: var(--text-light);
          font-size: 0.85rem;
        }
        .doc-actions {
          display: flex;
          gap: 0.5rem;
        }
        .action-btn {
          padding: 0.4rem 1rem;
          background: transparent;
          border: 1.5px solid var(--border);
          border-radius: var(--radius-sm);
          color: var(--text-muted);
          font-size: 0.85rem;
          font-weight: 700;
          font-family: var(--font-body);
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .action-btn:hover {
          border-color: var(--primary);
          color: var(--primary);
          background: var(--primary-bg);
        }
      `}</style>
    </div>
  );
};

export default Pharmacy;
